import { useState, useEffect, useCallback } from 'react';
import API from '../api/axios';
import { Icon } from '../components/SvgIcons';
import { getTenantUrl } from '../utils/tenant';

const MailIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: 4 }}>
    <rect x="2" y="4" width="20" height="16" rx="2"></rect>
    <path d="m22 7-10 6L2 7"></path>
  </svg>
);

const PhoneIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: 4 }}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  university_admin: 'University Admin',
  college_admin: 'College Admin',
  hod: 'HOD',
  moderator: 'Moderator',
  examiner: 'Examiner',
  subject_matter_expert: 'SME',
  coe: 'COE'
};

function formatTimeValue(value, unit) {
  if (unit === 'min') {
    if (value >= 1440) return { displayValue: value / 1440, displayUnit: 'day' };
    if (value >= 60) return { displayValue: value / 60, displayUnit: 'hr' };
    return { displayValue: value, displayUnit: 'min' };
  }
  if (unit === 'hr') {
    if (value >= 24) return { displayValue: value / 24, displayUnit: 'day' };
    return { displayValue: value, displayUnit: 'hr' };
  }
  return { displayValue: value, displayUnit: unit };
}

function TenantManagement() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantSlug, setSelectedTenantSlug] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  const [loadingTenants, setLoadingTenants] = useState(true);

  const fetchTenants = async () => {
    try {
      const { data } = await API.get('/superadmin/tenants');
      if (data.success) {
        setTenants(data.tenants || []);
      }
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
    } finally {
      setLoadingTenants(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const selectedTenant = tenants.find(t => t.slug === selectedTenantSlug);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Tenant Management</h1>
        <p className="dashboard-subtitle" style={{ color: 'white' }}>
          Manage users and settings across tenants
        </p>
      </div>

      <div className="card">
        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>Tenant:</label>
            {loadingTenants ? (
              <span style={{ color: 'var(--text-tertiary)' }}>Loading tenants...</span>
            ) : (
              <select
                value={selectedTenantSlug}
                onChange={(e) => setSelectedTenantSlug(e.target.value)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border-color)',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  minWidth: '250px',
                  cursor: 'pointer',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="">Select tenant</option>
                {tenants.map(t => (
                  <option key={t.slug} value={t.slug}>
                    {t.university_name} ({t.slug})
                  </option>
                ))}
              </select>
            )}
            {selectedTenant && (
              <span style={{
                fontSize: '0.75rem',
                fontWeight: 'bold',
                padding: '0.2rem 0.5rem',
                borderRadius: '4px',
                textTransform: 'capitalize',
                backgroundColor: selectedTenant.tenant_type === 'university' ? 'var(--success-light)' : 'var(--warning-light)',
                color: selectedTenant.tenant_type === 'university' ? 'var(--success)' : 'var(--warning)'
              }}>
                {selectedTenant.tenant_type || 'university'}
              </span>
            )}
          </div>
        </div>

        <div style={{ padding: '0 1rem', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 0' }}>
            <button
              className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => setActiveTab('users')}
            >
              Users
            </button>
            <button
              className={`btn ${activeTab === 'audit_logs' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => setActiveTab('audit_logs')}
            >
              Audit Logs
            </button>
            <button
              className={`btn ${activeTab === 'settings' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
          </div>
        </div>

        <div style={{ padding: '1rem' }}>
          {!selectedTenantSlug ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem 1rem',
              color: 'var(--text-secondary)',
              fontSize: '1rem',
              fontWeight: 500
            }}>
              Select tenant to view data
            </div>
          ) : (
            <>
              {activeTab === 'users' && <UsersTab tenantSlug={selectedTenantSlug} tenantType={selectedTenant?.tenant_type} />}
              {activeTab === 'audit_logs' && <AuditLogsTab tenantSlug={selectedTenantSlug} />}
              {activeTab === 'settings' && <SettingsTab tenantSlug={selectedTenantSlug} onTenantUpdated={(newSlug) => { setSelectedTenantSlug(newSlug); fetchTenants(); }} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, total, pageSize, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 0', borderTop: '1px solid var(--border-color)', marginTop: '1rem' }}>
      <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
        {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}
      </span>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
          className="btn btn-secondary btn-sm"
        >
          Previous
        </button>
        <button
          disabled={page === totalPages}
          onClick={() => onChange(page + 1)}
          className="btn btn-secondary btn-sm"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function AddUserModal({ tenantSlug, tenantType, onClose, onSuccess }) {
  const isUniTenant = tenantType === 'university';
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', role: '',
    college_id: '', department_id: '', subject_id: ''
  });
  const [colleges, setColleges] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [, setSubjects] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const roles = isUniTenant
    ? ['university_admin', 'college_admin', 'coe', 'hod', 'moderator', 'subject_matter_expert', 'examiner']
    : ['college_admin', 'hod', 'moderator', 'subject_matter_expert', 'examiner'];

  const needsCollege = ['college_admin', 'hod', 'moderator', 'subject_matter_expert', 'examiner'].includes(formData.role);
  const needsDepartment = ['hod', 'moderator', 'subject_matter_expert', 'examiner'].includes(formData.role);
  // Subject assignment removed from user creation - will be assigned after user creation

  useEffect(() => {
    if (needsCollege) {
      API.get(`/tenants/${tenantSlug}/colleges`).then(({ data }) => {
        if (data.success) setColleges(data.colleges || []);
      }).catch(() => {});
    }
  }, [tenantSlug, needsCollege]);

  useEffect(() => {
    if (needsDepartment && formData.college_id) {
      API.get(`/tenants/${tenantSlug}/departments`, { params: { college_id: formData.college_id } }).then(({ data }) => {
        setDepartments(data.departments || []);
      }).catch(() => {});
    }
  }, [tenantSlug, needsDepartment, formData.college_id]);
  // Subject fetching removed - will be assigned after user creation

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.role) { setError('Please select a role'); return; }
    if (!formData.name || !formData.email) {
      setError('Name and email are required'); return;
    }

    setSubmitting(true);
    try {
      const { data } = await API.post(`/tenants/${tenantSlug}/users`, formData);
      if (data.success) {
        onSuccess(data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'role') {
      setFormData({ ...formData, role: value, college_id: '', department_id: '' });
      setDepartments([]);
      setSubjects([]);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary)', borderRadius: '0.75rem',
        padding: '2rem', maxWidth: '520px', width: '90%',
        boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>Add User</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-tertiary)' }}>✕</button>
        </div>

        {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Role *</label>
            <select name="role" className="form-select" value={formData.role} onChange={handleChange} required>
              <option value="">Select Role</option>
              {roles.map(r => (
                <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input type="text" name="name" className="form-input" value={formData.name} onChange={handleChange} placeholder="Full name" required />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input type="email" name="email" className="form-input" value={formData.email} onChange={handleChange} placeholder="Email" required />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Phone</label>
            <input type="tel" name="phone" className="form-input" value={formData.phone} onChange={handleChange} placeholder="Phone number" />
          </div>

          <div style={{ background: 'var(--info-light, #e0f2fe)', color: 'var(--info, #0369a1)', borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            Temporary password set to <strong>Test@123</strong>
          </div>

          {needsCollege && (
            <div className="form-group">
              <label className="form-label">College *</label>
              <select name="college_id" className="form-select" value={formData.college_id} onChange={handleChange} required>
                <option value="">Select College</option>
                {colleges.map(c => (
                  <option key={c.college_id || c.id} value={c.college_id || c.id}>{c.college_name || c.name}</option>
                ))}
              </select>
            </div>
          )}

          {needsDepartment && (
            <div className="form-group">
              <label className="form-label">Department *</label>
              <select name="department_id" className="form-select" value={formData.department_id} onChange={handleChange} required disabled={!formData.college_id}>
                <option value="">Select Department</option>
                {departments.map(d => (
                  <option key={d.department_id || d.id} value={d.department_id || d.id}>{d.department_name || d.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Subject field removed - will be assigned after user creation */}

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UsersTab({ tenantSlug, tenantType }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [togglingId, setTogglingId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmImpersonateId, setConfirmImpersonateId] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [impersonateError, setImpersonateError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const pageSize = 10;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: currentPage, limit: pageSize };
      if (searchQuery) params.search = searchQuery;
      if (roleFilter) params.role = roleFilter;
      const { data } = await API.get(`/tenants/${tenantSlug}/users`, { params });
      if (data.success) {
        setUsers(data.users);
        setTotal(data.total);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, currentPage, searchQuery, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, roleFilter]);

  const handleToggleStatus = async (user) => {
    setTogglingId(user.id);
    try {
      await API.put(`/tenants/${tenantSlug}/users/${user.id}/status`, {
        is_active: !user.is_active
      });
      setUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, is_active: !u.is_active, status: u.is_active ? 'inactive' : 'active' } : u
      ));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleImpersonateClick = (userId) => {
    if (confirmImpersonateId === userId) {
      handleImpersonateConfirm(userId);
    } else {
      setConfirmImpersonateId(userId);
      setImpersonateError(null);
      setTimeout(() => setConfirmImpersonateId(null), 3000);
    }
  };

  const handleImpersonateConfirm = async (userId) => {
    setImpersonatingId(userId);
    setConfirmImpersonateId(null);
    setImpersonateError(null);
    try {
      const { data } = await API.post(`/tenants/${tenantSlug}/users/${userId}/impersonate`);
      if (data.success) {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        if (token) localStorage.setItem('admin_access_token_backup', token);
        if (userData) localStorage.setItem('admin_user_backup', userData);

        localStorage.setItem('is_impersonating', 'true');
        if (data.user) {
          localStorage.setItem('impersonation_target', JSON.stringify(data.user));
        }

        const targetUrl = getTenantUrl(tenantSlug);

        const params = new URLSearchParams({
          access_token: data.token,
          impersonating: 'true'
        });

        window.open(`${targetUrl}#${params.toString()}`, '_blank', 'noopener');
        setImpersonatingId(null);
      }
    } catch (err) {
      setImpersonateError(err.response?.data?.message || 'Impersonation failed');
      setImpersonatingId(null);
    }
  };

  const roles = [...new Set(users.map(u => u.role))];
  const totalPages = Math.ceil(total / pageSize);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Loading users...</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{error}</p>
        <button className="btn btn-primary btn-sm" onClick={fetchUsers}>Retry</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '0.375rem',
            border: '1px solid var(--border-color)',
            fontSize: '0.875rem',
            flex: 1,
            minWidth: '200px',
            maxWidth: '350px',
            outline: 'none',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)'
          }}
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '0.375rem',
            border: '1px solid var(--border-color)',
            fontSize: '0.875rem',
            cursor: 'pointer',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            outline: 'none'
          }}
        >
          <option value="">All Roles</option>
          {roles.map(r => (
            <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
          ))}
        </select>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary btn-sm"
        >
          + Add User
        </button>
        <button
          onClick={fetchUsers}
          className="btn btn-secondary btn-sm"
          style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem' }}
          title="Refresh users"
        >
          ↻
        </button>
      </div>

      {impersonateError && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          borderRadius: '0.375rem',
          fontSize: '0.875rem',
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)'
        }}>
          {impersonateError}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Joined</th>
              <th style={{ textAlign: 'center' }}>Active</th>
              <th style={{ textAlign: 'center' }}>Details</th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-tertiary)' }}>
                  No users found
                </td>
              </tr>
            ) : (
              users.map(user => {
                const isToggling = togglingId === user.id;
                return (
                  <tr key={user.id}>
                    <td>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{user.name}</div>
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{user.email}</td>
                    <td>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        backgroundColor: user.role === 'super_admin' ? 'var(--warning-light)' :
                          user.role === 'university_admin' ? 'var(--info-light)' :
                            user.role === 'college_admin' ? 'var(--primary-light)' :
                              user.role === 'moderator' ? '#f3e8ff' : 'var(--bg-tertiary)',
                        color: user.role === 'super_admin' ? 'var(--warning)' :
                          user.role === 'university_admin' ? 'var(--info)' :
                            user.role === 'college_admin' ? 'var(--primary)' :
                              user.role === 'moderator' ? '#6b21a8' : 'var(--text-secondary)'
                      }}>
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        backgroundColor: user.status === 'active' ? 'var(--success-light)' :
                          user.status === 'pending' ? 'var(--warning-light)' : 'var(--danger-light)',
                        color: user.status === 'active' ? 'var(--success)' :
                          user.status === 'pending' ? 'var(--warning)' : 'var(--danger)'
                      }}>
                        {user.status}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => handleToggleStatus(user)}
                        disabled={isToggling}
                        style={{
                          padding: '0.3rem 0.75rem',
                          borderRadius: '9999px',
                          border: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          cursor: isToggling ? 'not-allowed' : 'pointer',
                          opacity: isToggling ? 0.6 : 1,
                          backgroundColor: user.is_active ? 'var(--success-light)' : 'var(--bg-tertiary)',
                          color: user.is_active ? 'var(--success)' : 'var(--text-secondary)'
                        }}
                      >
                        {isToggling ? '...' : user.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => setSelectedUser(selectedUser?.id === user.id ? null : user)}
                        style={{
                          padding: '0.3rem 0.75rem',
                          borderRadius: '0.375rem',
                          border: '1px solid var(--border-color)',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          backgroundColor: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        View
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => handleImpersonateClick(user.id)}
                        disabled={impersonatingId === user.id}
                        style={{
                          padding: '0.3rem 0.75rem',
                          borderRadius: '0.375rem',
                          border: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          cursor: impersonatingId === user.id ? 'not-allowed' : 'pointer',
                          opacity: impersonatingId === user.id ? 0.6 : 1,
                          backgroundColor: confirmImpersonateId === user.id ? '#dc2626' : '#7c3aed',
                          color: 'white'
                        }}
                      >
                        {impersonatingId === user.id ? '...' :
                          confirmImpersonateId === user.id ? 'Confirm?' : 'Impersonate'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={currentPage} totalPages={totalPages} total={total} pageSize={pageSize} onChange={setCurrentPage} />

      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}

      {showAddModal && (
        <AddUserModal
          tenantSlug={tenantSlug}
          tenantType={tenantType}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
}

function UserDetailModal({ user, onClose }) {
  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary)',
        borderRadius: '0.75rem',
        padding: '2rem',
        maxWidth: '450px',
        width: '90%',
        boxShadow: 'var(--shadow-xl)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            User Details
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-tertiary)' }}>
            ✕
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <InfoRow label="Name" value={user.name} />
          <InfoRow label="Email" value={user.email} />
          <InfoRow label="Role" value={ROLE_LABELS[user.role] || user.role} />
          <InfoRow label="Status" value={user.status} />
          <InfoRow label="Department" value={user.department || '—'} />
          <InfoRow label="Phone" value={user.phone || '—'} />
          <InfoRow label="College ID" value={user.college_id ? String(user.college_id) : '—'} />
          <InfoRow label="University ID" value={user.university_id ? String(user.university_id) : '—'} />
          <InfoRow label="Joined" value={user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'} />
          <InfoRow label="Last Login" value={user.last_login ? new Date(user.last_login).toLocaleString() : '—'} />
        </div>

        <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
          <button onClick={onClose} className="btn btn-primary btn-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)' }}>
      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', minWidth: '110px' }}>{label}</span>
      <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)', textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

function SettingsTab({ tenantSlug, onTenantUpdated }) {
  const [tenantInfo, setTenantInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [editingTenant, setEditingTenant] = useState(false);
  const [tenantForm, setTenantForm] = useState({ slug: '', university_name: '' });
  const [tenantErrors, setTenantErrors] = useState({});
  const [savingTenant, setSavingTenant] = useState(false);
  const [tenantSaveMessage, setTenantSaveMessage] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await API.get(`/tenants/${tenantSlug}/settings`);
        if (data.success) {
          setTenantInfo(data.tenant);
          setSettings(data.settings);
          setTenantForm({ slug: data.tenant.slug, university_name: data.tenant.university_name });
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [tenantSlug]);

  useEffect(() => {
    if (tenantInfo) {
      setTenantForm({ slug: tenantInfo.slug, university_name: tenantInfo.university_name });
    }
  }, [tenantInfo]);

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      await API.put(`/tenants/${tenantSlug}/settings`, settings);
      setSaveMessage({ type: 'success', text: 'Session settings updated successfully' });
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update settings' });
    } finally {
      setSaving(false);
    }
  };

  const validateTenantForm = () => {
    const errors = {};
    if (!tenantForm.university_name.trim()) {
      errors.university_name = 'Tenant name is required';
    }
    if (!tenantForm.slug.trim()) {
      errors.slug = 'Tenant slug is required';
    } else if (!/^[a-z0-9-]+$/.test(tenantForm.slug)) {
      errors.slug = 'Slug must only contain lowercase letters, numbers, and hyphens';
    }
    setTenantErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveTenant = async () => {
    setTenantSaveMessage(null);
    setTenantErrors({});
    if (!validateTenantForm()) return;
    setSavingTenant(true);
    try {
      const { data } = await API.put(`/tenants/${tenantSlug}/tenant`, {
        slug: tenantForm.slug,
        university_name: tenantForm.university_name
      });
      if (data.success) {
        setTenantInfo(data.tenant);
        setEditingTenant(false);
        setTenantSaveMessage({ type: 'success', text: 'Tenant workspace updated successfully' });
        setTimeout(() => setTenantSaveMessage(null), 5000);
        if (onTenantUpdated && data.tenant.slug !== tenantSlug) {
          onTenantUpdated(data.tenant.slug);
        }
      }
    } catch (err) {
      setTenantSaveMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update tenant' });
    } finally {
      setSavingTenant(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingTenant(false);
    setTenantErrors({});
    setTenantForm({ slug: tenantInfo.slug, university_name: tenantInfo.university_name });
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Loading settings...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Tenant Workspace */}
      <div style={{
        border: '1px solid var(--border-color)',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        backgroundColor: 'var(--bg-primary)'
      }}>
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Tenant Workspace
              </h3>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Manage tenant name, URL slug, and workspace identity
              </p>
            </div>
            {!editingTenant && (
              <button
                onClick={() => setEditingTenant(true)}
                className="btn btn-secondary btn-sm"
                style={{ padding: '0.4rem 1rem' }}
              >
                Edit
              </button>
            )}
          </div>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {tenantSaveMessage && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              backgroundColor: tenantSaveMessage.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)',
              color: tenantSaveMessage.type === 'success' ? 'var(--success)' : 'var(--danger)'
            }}>
              {tenantSaveMessage.text}
            </div>
          )}

          {editingTenant ? (
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                  Tenant Name *
                </label>
                <input
                  type="text"
                  value={tenantForm.university_name}
                  onChange={(e) => setTenantForm({ ...tenantForm, university_name: e.target.value })}
                  style={{
                    width: '100%', padding: '0.6rem 0.75rem',
                    border: `1px solid ${tenantErrors.university_name ? '#dc2626' : 'var(--border-color)'}`,
                    borderRadius: '0.375rem', fontSize: '0.875rem',
                    outline: 'none', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)'
                  }}
                />
                {tenantErrors.university_name && (
                  <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: '0.35rem' }}>{tenantErrors.university_name}</div>
                )}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                  Tenant Slug *
                </label>
                <input
                  type="text"
                  value={tenantForm.slug}
                  onChange={(e) => setTenantForm({ ...tenantForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  style={{
                    width: '100%', padding: '0.6rem 0.75rem',
                    border: `1px solid ${tenantErrors.slug ? '#dc2626' : 'var(--border-color)'}`,
                    borderRadius: '0.375rem', fontSize: '0.875rem',
                    outline: 'none', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)',
                    fontFamily: 'monospace'
                  }}
                  placeholder="e.g. mum"
                />
                {tenantErrors.slug && (
                  <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: '0.35rem' }}>{tenantErrors.slug}</div>
                )}
                {tenantForm.slug && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.35rem' }}>
                    Resulting URL: <code style={{ fontSize: '0.75rem' }}>{getTenantUrl(tenantForm.slug)}</code>
                  </div>
                )}
              </div>

              {tenantInfo && (
                <div style={{ marginBottom: '1.25rem', padding: '0.75rem', borderRadius: '0.375rem', backgroundColor: 'var(--bg-secondary)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  <strong>Database:</strong> {tenantInfo.db_name} <em>(unchanged)</em>
                  <br />
                  <strong>Type:</strong> {tenantInfo.tenant_type || 'university'}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button onClick={handleCancelEdit} className="btn btn-secondary btn-sm" disabled={savingTenant}>
                  Cancel
                </button>
                <button onClick={handleSaveTenant} className="btn btn-primary btn-sm" disabled={savingTenant}>
                  {savingTenant ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Tenant Name</div>
                  <div style={{ fontSize: '0.95rem', color: 'var(--text-primary)', fontWeight: 500 }}>{tenantInfo?.university_name}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Tenant Slug</div>
                  <code style={{ fontSize: '0.85rem', backgroundColor: 'var(--bg-secondary)', padding: '0.2rem 0.4rem', borderRadius: '4px', color: 'var(--text-primary)' }}>
                    {tenantInfo?.slug}
                  </code>
                </div>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Tenant URL</div>
                  <a
                    href={getTenantUrl(tenantInfo?.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.85rem', color: 'var(--primary)', textDecoration: 'none', fontWeight: 500, wordBreak: 'break-all' }}
                  >
                    {getTenantUrl(tenantInfo?.slug)} ↗
                  </a>
                </div>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Database</div>
                  <code style={{ fontSize: '0.85rem', backgroundColor: 'var(--bg-secondary)', padding: '0.2rem 0.4rem', borderRadius: '4px', color: 'var(--text-primary)' }}>
                    {tenantInfo?.db_name}
                  </code>
                </div>
              </div>
              {(tenantInfo?.contact_email || tenantInfo?.phone_number) && (
                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)', display: 'flex', gap: '1.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  {tenantInfo.contact_email && <span><MailIcon /> {tenantInfo.contact_email}</span>}
                  {tenantInfo.phone_number && <span><PhoneIcon /> {tenantInfo.phone_number}</span>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <LogoSection tenantSlug={tenantSlug} tenantInfo={tenantInfo} />

      <div style={{
        border: '1px solid var(--border-color)',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        backgroundColor: 'var(--bg-primary)'
      }}>
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            JWT Session Settings
          </h3>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Configure authentication token lifespans and session behavior
          </p>
        </div>

        {saveMessage && (
          <div style={{
            margin: '1rem 1.5rem 0',
            padding: '0.75rem 1rem',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            backgroundColor: saveMessage.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)',
            color: saveMessage.type === 'success' ? 'var(--success)' : 'var(--danger)'
          }}>
            {saveMessage.text}
          </div>
        )}

        <div style={{ padding: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <SettingsSlider
              label="Access Token Lifespan"
              description="How long access tokens remain valid before requiring refresh"
              value={settings.access_token_lifespan_minutes}
              min={1} max={120}
              unit="min"
              onChange={(v) => setSettings({ ...settings, access_token_lifespan_minutes: v })}
            />
            <SettingsSlider
              label="Refresh Token Lifespan"
              description="How long refresh tokens remain valid"
              value={settings.refresh_token_lifespan_minutes}
              min={60} max={43200}
              unit="min"
              onChange={(v) => setSettings({ ...settings, refresh_token_lifespan_minutes: v })}
            />
            <SettingsSlider
              label="Session Duration"
              description="Maximum session duration regardless of activity"
              value={settings.session_duration_hours}
              min={1} max={72}
              unit="hr"
              onChange={(v) => setSettings({ ...settings, session_duration_hours: v })}
            />
            <SettingsSlider
              label="Idle Timeout"
              description="Auto-logout after this many minutes of inactivity"
              value={settings.idle_timeout_minutes}
              min={5} max={480} step={5}
              unit="min"
              onChange={(v) => setSettings({ ...settings, idle_timeout_minutes: v })}
            />
          </div>

          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            borderRadius: '0.375rem',
            border: '1px solid var(--border-color)',
            backgroundColor: 'var(--bg-secondary)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <label style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)', display: 'block', marginBottom: '0.25rem' }}>
                  Single-Use Refresh Tokens
                </label>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  Rotate refresh tokens on every use. Recommended for security.
                </p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, single_use_refresh_tokens: !settings.single_use_refresh_tokens })}
                style={{
                  width: '44px',
                  height: '24px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  position: 'relative',
                  backgroundColor: settings.single_use_refresh_tokens ? 'var(--primary)' : 'var(--border-hover)',
                  transition: 'background-color 0.2s',
                  flexShrink: 0
                }}
              >
                <span style={{
                  display: 'block',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  position: 'absolute',
                  top: '2px',
                  left: settings.single_use_refresh_tokens ? '22px' : '2px',
                  transition: 'left 0.2s'
                }} />
              </button>
            </div>
          </div>

          <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="btn btn-primary btn-sm"
              style={{ padding: '0.6rem 1.5rem' }}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsSlider({ label, description, value, min, max, step, unit, onChange }) {
  const pct = ((value - min) / (max - min)) * 100;
  const { displayValue, displayUnit } = formatTimeValue(value, unit);
  const displayStr = displayUnit === 'min' ? `${Math.round(displayValue)} min` :
    displayUnit === 'hr' ? `${displayValue % 1 === 0 ? Math.round(displayValue) : displayValue.toFixed(1)} hr` :
      `${displayValue % 1 === 0 ? Math.round(displayValue) : displayValue.toFixed(1)} day${displayValue >= 2 ? 's' : ''}`;
  return (
    <div>
      <label style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)', display: 'block', marginBottom: '0.25rem' }}>
        {label}
      </label>
      <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
        {description}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <input
          type="range"
          min={min}
          max={max}
          step={step || 1}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          style={{
            flex: 1,
            height: '6px',
            borderRadius: '3px',
            appearance: 'none',
            background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${pct}%, var(--border-color) ${pct}%, var(--border-color) 100%)`,
            outline: 'none',
            cursor: 'pointer'
          }}
        />
        <span style={{
          fontWeight: 700,
          fontSize: '0.9rem',
          color: 'var(--primary)',
          minWidth: '72px',
          textAlign: 'right'
        }}>
          {displayStr}
        </span>
      </div>
    </div>
  );
}

function LogoSection({ tenantSlug, tenantInfo }) {
  const [logoUrl, setLogoUrl] = useState(tenantInfo?.logo_url || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const apiUrl = process.env.REACT_APP_DEV_API_URL || 'http://localhost:5000';
    return `${apiUrl}${url}`;
  };

  useEffect(() => {
    setLogoUrl(tenantInfo?.logo_url || null);
  }, [tenantInfo]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a PNG, JPEG, WebP, or SVG image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('logo', file);
      const { data } = await API.post(`/tenants/${tenantSlug}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (data.success) {
        setLogoUrl(data.logo_url);
        setSuccess('Logo uploaded successfully');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      await API.delete(`/tenants/${tenantSlug}/logo`);
      setLogoUrl(null);
      setSuccess('Logo removed successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove logo');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: '0.5rem',
      overflow: 'hidden',
      backgroundColor: 'var(--bg-primary)'
    }}>
      <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Tenant Branding
        </h3>
        <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Upload a logo for your tenant workspace
        </p>
      </div>

      <div style={{ padding: '1.5rem' }}>
        {success && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            backgroundColor: 'var(--success-light)',
            color: 'var(--success)'
          }}>
            {success}
          </div>
        )}

        {error && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            backgroundColor: 'var(--danger-light)',
            color: 'var(--danger)'
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '0.5rem',
            border: '2px dashed var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: 'var(--bg-secondary)',
            flexShrink: 0
          }}>
            {logoUrl ? (
              <img
                src={getFullUrl(logoUrl)}
                alt="Tenant logo"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : (
              <span style={{ color: 'var(--text-tertiary)', display: 'inline-flex' }}><Icon name="Building" size={28} /></span>
            )}
          </div>

          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 0.75rem 0' }}>
              Accepted formats: PNG, JPEG, WebP, SVG (max 5MB)
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <label style={{ cursor: 'pointer' }}>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={handleUpload}
                  style={{ display: 'none' }}
                />
                <span className="btn btn-primary btn-sm" style={{ padding: '0.5rem 1rem' }}>
                  {isUploading ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
                </span>
              </label>
              {logoUrl && (
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="btn btn-secondary btn-sm"
                  style={{ padding: '0.5rem 1rem' }}
                >
                  {isDeleting ? 'Removing...' : 'Remove Logo'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatElapsed(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function AuditLogsTab({ tenantSlug }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: currentPage, limit: pageSize };
      if (tenantSlug) params.tenant_slug = tenantSlug;
      const { data } = await API.get('/tenants/impersonation/logs', { params });
      if (data.success) {
        setLogs(data.logs);
        setTotal(data.total);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, currentPage]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => { setCurrentPage(1); }, [tenantSlug]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return { bg: 'var(--success-light)', color: 'var(--success)' };
      case 'failed': return { bg: 'var(--danger-light)', color: 'var(--danger)' };
      case 'blocked': return { bg: 'var(--warning-light)', color: 'var(--warning)' };
      default: return { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)' };
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Loading audit logs...</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{error}</p>
        <button className="btn btn-primary btn-sm" onClick={fetchLogs}>Retry</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          {total} impersonation {total === 1 ? 'entry' : 'entries'}
        </span>
        <button
          onClick={fetchLogs}
          className="btn btn-secondary btn-sm"
          style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem' }}
          title="Refresh logs"
        >
          ↻
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Admin</th>
              <th>Target User</th>
              <th>Tenant</th>
              <th>Status</th>
              <th>IP Address</th>
              <th>Date</th>
              <th>Elapsed</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-tertiary)' }}>
                  No impersonation logs found
                </td>
              </tr>
            ) : (
              logs.map(log => {
                const sc = getStatusColor(log.status);
                return (
                  <tr key={log.id}>
                    <td>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{log.admin_name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{log.admin_email}</div>
                    </td>
                    <td>
                      <div style={{ color: 'var(--text-primary)' }}>{log.target_user_name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{log.target_user_email}</div>
                    </td>
                    <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{log.tenant_slug}</td>
                    <td>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        backgroundColor: sc.bg,
                        color: sc.color
                      }}>
                        {log.status}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{log.ip_address || '—'}</td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {formatDateTime(log.created_at)}
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                      {formatElapsed(log.created_at)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={currentPage} totalPages={totalPages} total={total} pageSize={pageSize} onChange={setCurrentPage} />
    </div>
  );
}

export default TenantManagement;
